import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import supabaseService from '../../services/supabaseService';
import Fun404Page from '../public/Fun404Page';

const ShortLinkRedirect = () => {
  const { shortCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        console.log('🔍 ShortLinkRedirect - Starting redirect for:', shortCode);
        console.log('🔍 Current URL:', window.location.href);
        console.log('🔍 Environment:', process.env.REACT_APP_ENVIRONMENT);

        // Look up the link by short code
        const link = await supabaseService.getByShortCode(shortCode);

        if (!link) {
          console.log('❌ Short code not found:', shortCode);

          // Check if current domain has a default destination
          const currentDomain = window.location.hostname;
          console.log('🌐 Current domain:', currentDomain);

          try {
            // Get domain configuration for current hostname
            const domains = await supabaseService.loadDomains();
            const domainConfig = domains.find(d => d.domain === currentDomain);

            if (domainConfig && domainConfig.defaultDestinationUrl) {
              console.log('🔄 Redirecting to default destination:', domainConfig.defaultDestinationUrl);
              window.location.href = domainConfig.defaultDestinationUrl;
              return;
            }
          } catch (domainError) {
            console.warn('⚠️ Could not load domain config:', domainError);
          }

          // Fallback to 404 page if no default destination
          setError('not_found');
          setLoading(false);
          return;
        }

        console.log('✅ Found link:', link);

        // Check if link is active
        if (!link.isActive) {
          console.log('❌ Link is inactive:', shortCode);
          setError('This link has been deactivated');
          setLoading(false);
          return;
        }

        // Check if link is expired
        if (link.expiresAt && new Date(link.expiresAt) <= new Date()) {
          console.log('❌ Link is expired:', shortCode);
          setError('This link has expired');
          setLoading(false);
          return;
        }

        // Increment click count
        try {
          await supabaseService.incrementClicks(link.id);
          console.log('📊 Click count incremented for link:', link.id);
        } catch (clickError) {
          console.warn('⚠️ Failed to increment click count:', clickError);
          // Don't block redirect if analytics fails
        }

        // Record analytics if needed
        try {
          const analyticsData = {
            ipAddress: null, // Will be null for privacy
            userAgent: navigator.userAgent,
            referrer: document.referrer,
            timestamp: new Date().toISOString()
          };

          await supabaseService.recordAnalytics(link.id, analyticsData);
          console.log('📈 Analytics recorded for link:', link.id);
        } catch (analyticsError) {
          console.warn('⚠️ Failed to record analytics:', analyticsError);
          // Don't block redirect if analytics fails
        }

        // Redirect to the original URL
        console.log('🔄 Redirecting to:', link.originalUrl);
        window.location.href = link.originalUrl;

      } catch (err) {
        console.error('❌ Error during redirect:', err);
        setError('An error occurred while processing the link');
        setLoading(false);
      }
    };

    if (shortCode) {
      handleRedirect();
    }
  }, [shortCode]);

  if (loading) {
    return (
      <div className="redirect-container">
        <div className="redirect-content">
          <div className="spinner"></div>
          <h2>Redirecting...</h2>
          <p>Please wait while we redirect you to your destination.</p>
        </div>

        <style jsx>{`
          .redirect-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          }

          .redirect-content {
            background: white;
            padding: 40px;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            text-align: center;
            max-width: 400px;
            width: 90%;
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f4f6;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          h2 {
            color: #333;
            margin-bottom: 10px;
            font-size: 24px;
            font-weight: 600;
          }

          p {
            color: #666;
            font-size: 16px;
            line-height: 1.5;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    // Show fun 404 page for not found links
    if (error === 'not_found') {
      return <Fun404Page />;
    }

    // Show regular error for other types of errors (expired, inactive, etc.)
    return (
      <div className="redirect-container">
        <div className="redirect-content error">
          <div className="error-icon">⚠️</div>
          <h2>Link Unavailable</h2>
          <p>{error}</p>
          <div className="action-buttons">
            <button
              className="home-button"
              onClick={() => window.location.href = 'https://trialconnx.com'}
            >
              Go to TrialConnx
            </button>
            <button
              className="back-button"
              onClick={() => window.history.back()}
            >
              Go Back
            </button>
          </div>
        </div>

        <style jsx>{`
          .redirect-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            padding: 20px;
          }

          .redirect-content {
            background: white;
            padding: 40px;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            text-align: center;
            max-width: 400px;
            width: 90%;
          }

          .redirect-content.error {
            border-top: 4px solid #ffc107;
          }

          .error-icon {
            font-size: 48px;
            margin-bottom: 20px;
          }

          h2 {
            color: #333;
            margin-bottom: 10px;
            font-size: 24px;
            font-weight: 600;
          }

          p {
            color: #666;
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 25px;
          }

          .action-buttons {
            display: flex;
            gap: 10px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .home-button,
          .back-button {
            border: none;
            padding: 12px 20px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .home-button {
            background: #667eea;
            color: white;
          }

          .home-button:hover {
            background: #5a6fd8;
            transform: translateY(-2px);
          }

          .back-button {
            background: #f8f9fa;
            color: #495057;
            border: 1px solid #dee2e6;
          }

          .back-button:hover {
            background: #e9ecef;
            transform: translateY(-2px);
          }

          @media (max-width: 480px) {
            .action-buttons {
              flex-direction: column;
            }

            .home-button,
            .back-button {
              width: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default ShortLinkRedirect;